import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { eventsService } from '../../services';
import { LoadingPage } from '../../components/ui/Loading';
import { isAdminEquivalent } from '../../utils/permissions';

const AdminDeclinedEvents = () => {
  const { user } = useAuth();
  const [declinedParticipations, setDeclinedParticipations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    hasReason: 'all', // all, yes, no
    sortBy: 'recent' // recent, oldest
  });

  useEffect(() => {
    loadDeclinedParticipations();
  }, [filters]);

  const loadDeclinedParticipations = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Buscar participações declinadas diretamente do backend
      const response = await eventsService.getDeclinedParticipations();
      const declined = response.data || [];

      // Aplicar filtros
      let filtered = declined;
      
      if (filters.hasReason === 'yes') {
        filtered = filtered.filter(p => p.declineReason && p.declineReason.trim() !== '');
      } else if (filters.hasReason === 'no') {
        filtered = filtered.filter(p => !p.declineReason || p.declineReason.trim() === '');
      }

      // Ordenar
      if (filters.sortBy === 'recent') {
        filtered.sort((a, b) => new Date(b.declinedAt || b.createdAt) - new Date(a.declinedAt || a.createdAt));
      } else {
        filtered.sort((a, b) => new Date(a.declinedAt || a.createdAt) - new Date(b.declinedAt || b.createdAt));
      }

      setDeclinedParticipations(filtered);
    } catch (err) {
      console.error('Error loading declined participations:', err);
      setError('Erro ao carregar ausências informadas');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdminEquivalent(user?.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-8 text-center">
            <p className="text-red-400">Acesso negado. Apenas administradores podem visualizar esta página.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingPage />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Card */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-6">
          <div className="flex items-center space-x-4">
            <Link
              to="/app/admin"
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-gray-400" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                🔒 Ausências Informadas
              </h1>
              <p className="mt-1 text-sm text-gray-400">
                Visualize todas as justificativas de ausências informadas pelos membros
              </p>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Filtrar por justificativa
              </label>
              <select
                value={filters.hasReason}
                onChange={(e) => setFilters({ ...filters, hasReason: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              >
                <option value="all">Todas</option>
                <option value="yes">Com justificativa</option>
                <option value="no">Sem justificativa</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Ordenar por
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              >
                <option value="recent">Mais recentes</option>
                <option value="oldest">Mais antigas</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-6 text-center">
            <div className="text-3xl font-bold text-white">{declinedParticipations.length}</div>
            <div className="text-sm text-gray-400">Total de Ausências</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-6 text-center">
            <div className="text-3xl font-bold text-green-400">
              {declinedParticipations.filter(p => p.declineReason && p.declineReason.trim() !== '').length}
            </div>
            <div className="text-sm text-gray-400">Com Justificativa</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-6 text-center">
            <div className="text-3xl font-bold text-red-400">
              {declinedParticipations.filter(p => !p.declineReason || p.declineReason.trim() === '').length}
            </div>
            <div className="text-sm text-gray-400">Sem Justificativa</div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* List */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-xl p-6">
          <h2 className="text-xl font-semibold text-white mb-4">
            Lista de Ausências ({declinedParticipations.length})
          </h2>

          {declinedParticipations.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <div className="text-4xl mb-2">🎉</div>
              <p>Nenhuma ausência informada!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {declinedParticipations.map((participation) => (
                <div
                  key={participation.id}
                  className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-4 hover:border-orange-500/50 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      {/* Event Info */}
                      <div>
                        <h3 className="font-semibold text-white text-lg">
                          {participation.eventName}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400 mt-1">
                          <span>📅 {new Date(participation.eventStartDate).toLocaleDateString('pt-BR')}</span>
                          <span>•</span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-gray-300 border border-white/10">
                            {participation.eventType}
                          </span>
                        </div>
                      </div>

                      {/* User Info */}
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">
                            {participation.character?.name?.charAt(0) || participation.user?.name?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-white">
                            {participation.user?.name || 'Usuário'}
                          </div>
                          <div className="text-xs text-gray-400">
                            {participation.character?.name} • Nv. {participation.character?.level}
                          </div>
                        </div>
                      </div>

                      {/* Decline Info */}
                      <div className="text-xs text-gray-400">
                        <span className="font-medium">Informado em:</span>{' '}
                        {participation.declinedAt 
                          ? new Date(participation.declinedAt).toLocaleString('pt-BR')
                          : new Date(participation.createdAt).toLocaleString('pt-BR')
                        }
                      </div>

                      {/* Reason */}
                      {participation.declineReason && participation.declineReason.trim() !== '' ? (
                        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mt-2">
                          <div className="flex items-start gap-2">
                            <span className="text-orange-400 flex-shrink-0">💬</span>
                            <div>
                              <div className="text-xs font-medium text-orange-400 mb-1">
                                Justificativa:
                              </div>
                              <p className="text-sm text-gray-300">
                                {participation.declineReason}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-3 mt-2">
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <span>⚠️</span>
                            <span className="italic">Sem justificativa informada</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDeclinedEvents;
